import React, { useEffect, useRef, useState } from "react";
import WordMorphology from "../WordMorphology";
import { fetchOnboardingAudioManifest, onboardingAudioUrl } from "../../api";
import { track } from "../../utils/analytics";
import { TOPIC_DEMO } from "./topicDemoContent";

/**
 * OnboardingTopicDemo — after the Quran verse, a taste of a real speaking
 * topic: a two-line scenario exchange where every word is tappable, opening
 * a tooltip with translation, voice-over play, and the word-structure modal.
 *
 * Text data is bundled (anonymous visitors, no quota). Voice-over streams
 * from the public whitelisted-clip endpoint — real lesson TTS voices,
 * disk-cached server-side after the first-ever play and browser-cached per
 * visitor. Play buttons appear only when the manifest says TTS is live, so
 * an unconfigured server degrades to text-only, never a dead button.
 */
export default function OnboardingTopicDemo({ onContinue }) {
  const [selected, setSelected] = useState(null); // { lineKey, word }
  const [structureWord, setStructureWord] = useState(null); // morphology
  const [audioKeys, setAudioKeys] = useState(null); // Set | null = none
  const [playingKey, setPlayingKey] = useState(null);
  // Coach cues: pulse the first word until one is tapped, then the
  // Structure button until the modal has been seen once.
  const [wordTapped, setWordTapped] = useState(false);
  const [structSeen, setStructSeen] = useState(false);
  const audioRef = useRef(null);
  const tapped = useRef(false);

  useEffect(() => {
    fetchOnboardingAudioManifest()
      .then((m) => {
        if (m?.enabled && Array.isArray(m.keys)) setAudioKeys(new Set(m.keys));
      })
      .catch(() => {});
    return () => audioRef.current?.pause();
  }, []);

  const play = (key) => {
    try {
      audioRef.current?.pause();
      const audio = new Audio(onboardingAudioUrl(key));
      audioRef.current = audio;
      setPlayingKey(key);
      audio.addEventListener("ended", () => setPlayingKey(null));
      audio.play().catch(() => setPlayingKey(null));
      track("onboarding-topic-play", { key });
    } catch {
      setPlayingKey(null);
    }
  };

  const tapWord = (lineKey, word) => {
    if (selected?.word.key === word.key) {
      setSelected(null);
      return;
    }
    setSelected({ lineKey, word });
    setWordTapped(true);
    if (!tapped.current) {
      tapped.current = true;
      track("onboarding-topic-word-tap", {});
    }
  };

  return (
    <div className="w-full">
      <h2 className="text-xl sm:text-2xl font-semibold text-dt-parchment mb-1.5">
        Your lessons work the same way
      </h2>
      <p className="text-sm text-dt-parchment-dim mb-4">{TOPIC_DEMO.intro}</p>

      <div className="surface rounded-xl px-4 py-4">
        <p className="text-xs uppercase tracking-[0.2em] text-dt-gold-dim mb-3 select-none">
          ✦ {TOPIC_DEMO.topicTitle}
        </p>
        {TOPIC_DEMO.lines.map((line) => (
          <div key={line.key} className="mb-4 last:mb-0">
            <div className="flex items-center gap-2 mb-1">
              <span dir="rtl" lang="ar" className="font-amiri text-sm text-dt-gold-bright">
                {line.speaker}
              </span>
              <span className="text-[11px] text-dt-parchment-dim">{line.speakerEn}</span>
              {audioKeys?.has(line.key) && (
                <button
                  type="button"
                  onClick={() => play(line.key)}
                  aria-label={`Play ${line.speakerEn}'s line`}
                  className="text-xs text-dt-parchment-dim hover:text-dt-gold-bright transition-colors"
                >
                  {playingKey === line.key ? "▮▮" : "▶"}
                </button>
              )}
            </div>
            <div dir="rtl" lang="ar" className="flex flex-wrap gap-x-2 gap-y-2 font-amiri text-2xl leading-loose">
              {line.words.map((word) => (
                <button
                  key={word.key}
                  type="button"
                  onClick={() => tapWord(line.key, word)}
                  className={`bg-transparent border-0 p-0 font-amiri text-2xl leading-loose cursor-pointer transition-colors ${
                    selected?.word.key === word.key ? "text-dt-gold-bright" : "text-dt-parchment hover:text-dt-gold-bright"
                  } ${!wordTapped && word.key === "l1w0" ? "coach-pulse" : ""}`}
                >
                  {word.text}
                </button>
              ))}
            </div>

            {/* Word tooltip — inline under its line, like the lesson bubble */}
            {selected?.lineKey === line.key && (
              <div className="surface-glass rounded-xl px-3.5 py-3 mt-2 animate-rise">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-dt-parchment font-medium">
                      {selected.word.gloss}
                    </p>
                    <p className="text-xs text-dt-parchment-dim italic">{selected.word.translit}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {audioKeys?.has(selected.word.key) && (
                      <button
                        type="button"
                        onClick={() => play(selected.word.key)}
                        className="px-2.5 py-1.5 rounded-lg border text-xs text-dt-parchment-dim hover:text-dt-gold-bright transition-colors"
                        style={{ borderColor: "var(--panel-line)" }}
                      >
                        {playingKey === selected.word.key ? "▮▮" : "▶"} Listen
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setStructureWord(selected.word.morphology);
                        setStructSeen(true);
                        track("onboarding-topic-structure", {});
                      }}
                      className={`px-2.5 py-1.5 rounded-lg border text-xs text-dt-parchment-dim hover:text-dt-gold-bright transition-colors ${
                        structSeen ? "" : "coach-pulse"
                      }`}
                      style={{ borderColor: "var(--panel-line)" }}
                    >
                      ◇ Structure
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-dt-parchment-dim mt-1.5">{line.translation}</p>
          </div>
        ))}
      </div>

      <p className="text-xs text-dt-parchment-dim mt-3">
        Tap any word — translation, voice-over and grammar structure, in every
        lesson, on every line.
      </p>

      <button
        type="button"
        onClick={onContinue}
        className="w-full mt-5 px-4 py-3 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter]"
      >
        Continue
      </button>

      {structureWord && (
        <WordMorphology word={structureWord} onClose={() => setStructureWord(null)} />
      )}
    </div>
  );
}
